import { FC, useState } from 'react';
import { IonButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { Card } from '../components/Card';
import { Text } from '../components/Text';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { UserState } from '../user/UserState';
import { AlertPresenter } from '../AlertBinder';

interface ITaskNotificationTestResponse {
    scenario: string;
    stats: {
        completedToday: number;
        dueToday: number;
        overdue: number;
        dueTomorrow: number;
        hasActiveTasks: boolean;
    };
    message: {
        title: string;
        body: string;
    };
    userId: string;
}

export const TasksNotificationDevTools: FC = () => {
    const apiClient = useInstance(APIClient);
    const userState = useInstance(UserState);
    const alertPresenter = useInstance(AlertPresenter);
    const [loading, setLoading] = useState(false);

    const testNotification = async () => {
        setLoading(true);
        try {
            // Use the test link from user's links (standard HATEOAS pattern)
            const testLink = userState.state?.links?.testTasksNotification;
            if (!testLink) {
                throw new Error('Test endpoint not available. Please reload the app.');
            }

            const response = await apiClient.get(testLink) as ITaskNotificationTestResponse;

            const statsText = `
Completed Today: ${response.stats.completedToday}
Due Today: ${response.stats.dueToday}
Overdue: ${response.stats.overdue}
Due Tomorrow: ${response.stats.dueTomorrow}
Has Active Tasks: ${response.stats.hasActiveTasks}
            `.trim();

            await alertPresenter.showAlertV2({
                header: `Scenario: ${response.scenario}`,
                subHeader: response.message.title,
                message: `${response.message.body}\n\n━━━━━━━━━━━━━━━━━━━━\n\nTask Statistics:\n${statsText}`,
                backdropDismiss: true,
                options: [{
                    id: 'ok',
                    label: 'OK'
                }]
            });
        } catch (error: any) {
            await alertPresenter.showAlertV2({
                header: 'Error',
                message: error.message || 'Failed to test notification',
                backdropDismiss: true,
                options: [{
                    id: 'ok',
                    label: 'OK'
                }]
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{marginBottom: '16px'}}>
            <Card>
                <div style={{padding: '16px'}}>
                    <div style={{marginBottom: '12px'}}>
                        <Text><b>Task Notification Dev Tools</b></Text>
                    </div>
                    <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                        <IonButton
                            fill="outline"
                            color="secondary"
                            size="small"
                            data-id="test-task-notification-button"
                            onClick={testNotification}
                            disabled={loading}
                        >
                            {loading ? 'Testing...' : 'Test My Notification'}
                        </IonButton>
                    </div>
                </div>
            </Card>
        </div>
    );
};
